// Investment Amenities Selector Component
// Compact chips for selecting amenities
// Stores data as JSON array of amenity IDs

import {
  Component,
  Input,
  forwardRef,
  ChangeDetectionStrategy,
  signal,
  computed,
} from '@angular/core';

import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

// PrimeNG
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';

import {
  AmenityDefinition,
  AmenityCategory,
  DEFAULT_AMENITIES,
  CATEGORY_LABELS,
} from './investment-description-enhanced.component';

@Component({
  selector: 'app-amenities-selector',
  standalone: true,
  imports: [
    TooltipModule,
    RippleModule
],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AmenitiesSelectorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="amenities-selector" [class.disabled]="disabled">
      @for (category of categories; track category) {
        <div class="category-group">
          <div class="category-label">{{ getCategoryLabel(category) }}</div>
          <div class="amenity-chips">
            @for (amenity of getAmenitiesByCategory(category); track amenity.id) {
              <button
                type="button"
                class="amenity-chip"
                [class.selected]="isSelected(amenity.id)"
                [class]="'amenity-chip ' + (isSelected(amenity.id) ? 'selected category-' + amenity.category : '')"
                [disabled]="disabled"
                (click)="toggleAmenity(amenity.id)"
                pRipple
              >
                <i [class]="'pi ' + amenity.icon"></i>
                <span>{{ amenity.label }}</span>
                @if (isSelected(amenity.id)) {
                  <i class="pi pi-check check-icon"></i>
                }
              </button>
            }
          </div>
        </div>
      }
      
      <div class="counter">
        <i class="pi pi-check-circle"></i>
        {{ selectedCount() }} udogodnień wybranych
      </div>
    </div>
  `,
  styles: [`
    .amenities-selector {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .amenities-selector.disabled {
      opacity: 0.6;
      pointer-events: none;
    }

    /* Category Groups */
    .category-group {
      margin-bottom: 0.25rem;
    }

    .category-label {
      font-size: 0.625rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: var(--p-text-secondary-color);
      margin-bottom: 0.5rem;
      padding-left: 0.125rem;
    }

    /* Chips Layout */
    .amenity-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    /* Chip Styling */
    .amenity-chip {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.5rem 0.75rem;
      background: var(--p-surface-50);
      border: 1.5px solid var(--p-surface-200);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.15s;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--p-text-color);
    }

    .amenity-chip i {
      font-size: 0.875rem;
    }

    .amenity-chip:hover:not(:disabled):not(.selected) {
      background: var(--p-surface-100);
      border-color: var(--p-surface-300);
    }

    .amenity-chip.selected {
      border-width: 2px;
      padding: calc(0.5rem - 0.5px) calc(0.75rem - 0.5px);
    }

    .check-icon {
      font-size: 0.625rem !important;
      margin-left: 0.125rem;
    }

    /* Category-specific colors when selected */
    .amenity-chip.category-building {
      background: #eff6ff;
      border-color: #3b82f6;
      color: #1d4ed8;
    }

    .amenity-chip.category-outdoor {
      background: #ecfdf5;
      border-color: #10b981;
      color: #047857;
    }

    .amenity-chip.category-security {
      background: #fffbeb;
      border-color: #f59e0b;
      color: #b45309;
    }

    .amenity-chip.category-comfort {
      background: #fdf4ff;
      border-color: #a855f7;
      color: #a21caf;
    }

    .amenity-chip.category-eco {
      background: #f0fdf4;
      border-color: #22c55e;
      color: #15803d;
    }

    /* Counter */
    .counter {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--p-text-secondary-color);
      padding: 0.75rem;
      background: var(--p-surface-50);
      border-radius: 8px;
      border: 1px solid var(--p-surface-200);
    }

    .counter i {
      color: #10b981;
    }

    /* Responsive */
    @media (max-width: 640px) {
      .amenity-chip {
        padding: 0.375rem 0.625rem;
        font-size: 0.75rem;
      }

      .amenity-chip i {
        font-size: 0.75rem;
      }
    }
  `]
})
export class AmenitiesSelectorComponent implements ControlValueAccessor {
  @Input() disabled = false;
  @Input() amenities: AmenityDefinition[] = DEFAULT_AMENITIES;

  selectedAmenities = signal<string[]>([]);
  categories: AmenityCategory[] = ['building', 'outdoor', 'security', 'comfort', 'eco'];

  // ControlValueAccessor
  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  selectedCount = computed(() => this.selectedAmenities().length);

  writeValue(value: string | string[] | null): void {
    if (value) {
      // Handle both JSON string and array
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          this.selectedAmenities.set(Array.isArray(parsed) ? parsed : []);
        } catch {
          this.selectedAmenities.set([]);
        }
      } else if (Array.isArray(value)) {
        this.selectedAmenities.set(value);
      }
    } else {
      this.selectedAmenities.set([]);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggleAmenity(id: string): void {
    const current = this.selectedAmenities();
    const isSelected = current.includes(id);

    if (isSelected) {
      this.selectedAmenities.set(current.filter(a => a !== id));
    } else {
      this.selectedAmenities.set([...current, id]);
    }

    this.emitChange();
    this.onTouched();
  }

  isSelected(id: string): boolean {
    return this.selectedAmenities().includes(id);
  }

  getAmenitiesByCategory(category: AmenityCategory): AmenityDefinition[] {
    return this.amenities.filter(a => a.category === category);
  }

  getCategoryLabel(category: AmenityCategory): string {
    return CATEGORY_LABELS[category];
  }

  private emitChange(): void {
    const jsonString = JSON.stringify(this.selectedAmenities());
    this.onChange(jsonString);
  }
}
